import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  getAssetSummary,
  getEffectiveFormData,
  getMaintenanceEventContext,
  getPublishedFormConfig,
  writeFormValues,
  type AssetSummary,
} from "@/lib/wartungen";
import {
  stripSignatureScopedValues,
} from "./lib/maintenanceEdit";
import {
  coerceGradeValue,
  computeAutoGrade,
  formatFieldDisplayValue,
  getFieldLabel,
  getGradeColor,
  isValueEmpty,
  normalizeEnabledGrades,
  normalizeFields,
  splitIntoPages,
  type NormalizedField,
  type RuntimeScope,
} from "./form/runtimeModel";
import { ReadonlyMaintenance } from "./form/ReadonlyMaintenance";
import { RuntimeField } from "./form/RuntimeField";

type Values = Record<RuntimeScope, Record<string, unknown>>;

function emptyValues(): Values {
  return { masterdata: {}, inspection: {} };
}

function num(params: URLSearchParams, key: string): number | null {
  const n = Number(params.get(key));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function safeInternalPath(raw: string | null): string | null {
  const value = String(raw || "").trim();
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

export function FormPage() {
  const [params] = useSearchParams();
  const initialTypeKey = (params.get("type_key") || params.get("type") || "").trim();
  const eventIdParam = num(params, "maintenance_event_id");
  const assetIdParam = num(params, "asset_id");
  const viewOnly = params.get("view") === "1";
  const returnTo = safeInternalPath(params.get("return_url"));

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ tone: "success" | "error" | "warning"; messages: string[] } | null>(null);
  const [saving, setSaving] = useState(false);

  const [fields, setFields] = useState<NormalizedField[]>([]);
  const [values, setValues] = useState<Values>(emptyValues());
  const [enabledGrades, setEnabledGrades] = useState<number[]>(normalizeEnabledGrades(null));
  const [gradeManual, setGradeManual] = useState("");
  const [gradeReason, setGradeReason] = useState("");
  const [storedFinalGrade, setStoredFinalGrade] = useState<number | null>(null);
  const [assetSummary, setAssetSummary] = useState<AssetSummary | null>(null);
  const [resolutionLabel, setResolutionLabel] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);

  const ctx = useRef<{
    typeKey: string;
    formId: number | null;
    assetId: number | null;
    eventId: number | null;
    contractId: number | null;
    heroAddressId: number | null;
  }>({
    typeKey: initialTypeKey,
    formId: null,
    assetId: assetIdParam,
    eventId: eventIdParam,
    contractId: null,
    heroAddressId: null,
  });

  const valuesRef = useRef<Values>(values);
  valuesRef.current = values;

  const collectFlatValues = useCallback((fieldList: NormalizedField[], vals: Values): Record<string, unknown> => {
    const acc: Record<string, unknown> = {};
    for (const field of fieldList) {
      if (field.effective_field_type === "page_break" || field.effective_field_type === "divider") continue;
      if (field.effective_scope === "masterdata") {
        // Stammdaten sind Quellen für Sichtbarkeitsregeln und müssen mitgesendet werden,
        // auch wenn sie im Formular nur angezeigt werden.
        if (field.field_key in (vals.masterdata || {})) {
          acc[field.field_key] = vals.masterdata[field.field_key] ?? null;
        }
        continue;
      }
      if (!field.resolved_visible || !field.resolved_enabled) continue;
      acc[field.field_key] = vals[field.effective_scope]?.[field.field_key] ?? null;
    }
    return acc;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const editCompletedParam = params.get("edit_completed") === "1";
    void (async () => {
      setLoading(true);
      setFatalError(null);
      try {
        if (eventIdParam == null && !initialTypeKey) {
          setFatalError("Unvollständige Query-Parameter: Bitte type oder maintenance_event_id angeben.");
          return;
        }

        let eventContextEditable = true;
        let eventContextCompleted = false;

        if (eventIdParam != null) {
          try {
            const eventContext = await getMaintenanceEventContext(eventIdParam);
            eventContextEditable = eventContext.editable !== false;
            eventContextCompleted = eventContext.is_completed_event === true;
            if (eventContext.project_completed === true && !viewOnly) {
              setFatalError("Das verknüpfte Projekt ist fertiggestellt. Bearbeitung ist nicht mehr möglich.");
              return;
            }
            ctx.current.typeKey = ctx.current.typeKey || String(eventContext.type_key || "").trim();
            if (ctx.current.formId == null && Number(eventContext.form_id) > 0) ctx.current.formId = Number(eventContext.form_id);
            if (ctx.current.assetId == null && Number(eventContext.asset_id) > 0) ctx.current.assetId = Number(eventContext.asset_id);
            ctx.current.contractId = eventContext.contract_id != null ? Number(eventContext.contract_id) : null;
            const heroAddr = Number(eventContext.project?.hero_address_id ?? eventContext.hero_address_id);
            if (Number.isFinite(heroAddr) && heroAddr > 0) ctx.current.heroAddressId = heroAddr;
            setGradeManual(String(eventContext.grade_manual ?? "").trim());
            setGradeReason(String(eventContext.grade_override_reason ?? "").trim());
            setStoredFinalGrade(coerceGradeValue(eventContext.grade_final));
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              setFatalError("Maintenance-Event wurde nicht gefunden.");
              return;
            }
            setFatalError("Kontext zum Maintenance-Event konnte nicht geladen werden.");
            return;
          }
        }

        if (!ctx.current.typeKey || ctx.current.assetId == null || ctx.current.eventId == null) {
          setFatalError("Dieses Formular kann nur mit asset_id und maintenance_event_id geöffnet werden.");
          return;
        }

        const schema = await getPublishedFormConfig({
          typeKey: ctx.current.typeKey,
          formId: ctx.current.formId,
          contractId: ctx.current.contractId,
          heroAddressId: ctx.current.heroAddressId,
        });
        ctx.current.formId = Number(schema.form_id) || ctx.current.formId;

        const schemaFields = normalizeFields(schema);
        const initialValues = emptyValues();
        for (const field of schemaFields) {
          if (!Object.hasOwn(initialValues[field.effective_scope], field.field_key)) {
            initialValues[field.effective_scope][field.field_key] = field.value ?? null;
          }
        }

        // Effektive Daten (Werte + Sichtbarkeit) anhand der Initialwerte ziehen.
        let resolvedFields = schemaFields;
        let gradeConfig = schema.grade_config_json ?? null;
        try {
          const effective = await getEffectiveFormData(ctx.current.formId!, {
            asset_id: ctx.current.assetId,
            maintenance_event_id: ctx.current.eventId,
            values: collectFlatValues(schemaFields, initialValues),
          });
          const effFields = normalizeFields(effective);
          if (effFields.length) {
            resolvedFields = effFields;
            gradeConfig = effective.grade_config_json ?? gradeConfig;
          }
        } catch {
          /* Fallback auf Schema-Felder */
        }

        for (const field of resolvedFields) {
          if (field.value !== undefined) initialValues[field.effective_scope][field.field_key] = field.value;
        }

        const editingCompleted =
          !viewOnly && (editCompletedParam || (eventContextCompleted && eventContextEditable));
        if (editingCompleted) {
          const stripped = stripSignatureScopedValues(resolvedFields, initialValues);
          initialValues.masterdata = stripped.masterdata;
          initialValues.inspection = stripped.inspection;
        }

        if (cancelled) return;
        setIsEditingCompleted(editingCompleted);
        setFields(resolvedFields);
        setValues(initialValues);
        setEnabledGrades(normalizeEnabledGrades(gradeConfig));

        const source = String(schema.published?.resolution_source || "").trim();
        setResolutionLabel(
          source === "contract_location_mapping"
            ? "Vertrags-/Ort-Mapping"
            : source === "category_default"
              ? "Kategorie-Standard"
              : "Published-Fallback",
        );

        try {
          const summary = await getAssetSummary(ctx.current.typeKey, ctx.current.assetId!, ctx.current.eventId);
          if (!cancelled) setAssetSummary(summary);
        } catch {
          if (!cancelled) setAssetSummary({ external_id: String(ctx.current.assetId), name: "", last_performed_at: null });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdParam, assetIdParam, initialTypeKey]);

  // Debounced Re-Resolve von Sichtbarkeit + Antwortautomatik, wenn sich Werte ändern.
  const resolveTimer = useRef<number | null>(null);
  const changedFieldRef = useRef<string | null>(null);
  const refreshVisibility = useCallback(() => {
    if (!ctx.current.formId || ctx.current.assetId == null) return;
    if (resolveTimer.current) window.clearTimeout(resolveTimer.current);
    resolveTimer.current = window.setTimeout(async () => {
      try {
        const changedFieldKey = changedFieldRef.current;
        const effective = await getEffectiveFormData(ctx.current.formId!, {
          asset_id: ctx.current.assetId,
          maintenance_event_id: ctx.current.eventId,
          values: collectFlatValues(fields, valuesRef.current),
          changed_field_key: changedFieldKey,
        });
        const effFields = normalizeFields(effective);
        if (effFields.length) {
          setFields(effFields);
          // Server-seitig berechnete Antwortwerte (Antwortautomatik inkl. Rücksprung)
          // in den lokalen State übernehmen. Das aktuell bearbeitete Feld bleibt
          // unangetastet, damit laufende Eingaben nicht überschrieben werden.
          setValues((prev) => {
            const next: Values = {
              masterdata: { ...prev.masterdata },
              inspection: { ...prev.inspection },
            };
            for (const f of effFields) {
              if (f.value === undefined) continue;
              if (changedFieldRef.current && f.field_key === changedFieldRef.current) continue;
              next[f.effective_scope][f.field_key] = f.value ?? null;
            }
            return next;
          });
          if (effective.grade_config_json) setEnabledGrades(normalizeEnabledGrades(effective.grade_config_json));
        }
      } catch {
        /* ignore */
      }
    }, 400);
  }, [fields, collectFlatValues]);

  function setFieldValue(field: NormalizedField, value: unknown) {
    changedFieldRef.current = field.field_key;
    setValues((prev) => ({
      ...prev,
      [field.effective_scope]: { ...prev[field.effective_scope], [field.field_key]: value },
    }));
    setFieldErrors((prev) => {
      if (!prev[field.field_key]) return prev;
      const next = { ...prev };
      delete next[field.field_key];
      return next;
    });
    refreshVisibility();
  }

  const masterdataFields = useMemo(() => fields.filter((f) => f.effective_scope === "masterdata"), [fields]);
  const inspectionFields = useMemo(() => fields.filter((f) => f.effective_scope === "inspection"), [fields]);
  const pages = useMemo(() => splitIntoPages(inspectionFields), [inspectionFields]);
  const totalSteps = pages.length;
  const safeStep = Math.min(currentStep, Math.max(totalSteps - 1, 0));

  const autoGrade = useMemo(() => computeAutoGrade(fields, values), [fields, values]);
  const manualGrade = coerceGradeValue(gradeManual);
  const effectiveGrade = manualGrade ?? (viewOnly ? storedFinalGrade ?? autoGrade : autoGrade);

  function validate(): string[] {
    const errors: string[] = [];
    const map: Record<string, string> = {};
    for (const field of fields) {
      if (field.effective_scope === "masterdata") continue;
      if (field.effective_field_type === "page_break" || field.effective_field_type === "divider") continue;
      if (!field.resolved_visible || !field.resolved_enabled || !field.resolved_required) continue;
      const value = values[field.effective_scope]?.[field.field_key];
      if (isValueEmpty(value)) {
        const msg = `${getFieldLabel(field)}: Pflichtfeld fehlt.`;
        errors.push(msg);
        map[field.field_key] = "Pflichtfeld fehlt.";
      }
    }
    setFieldErrors(map);
    return errors;
  }

  async function handleSave() {
    const errors = validate();
    if (errors.length) {
      setFeedback({ tone: "error", messages: errors });
      return;
    }
    if (!ctx.current.formId || ctx.current.assetId == null || ctx.current.eventId == null) {
      setFeedback({ tone: "warning", messages: ["Speichern ist nur mit form_id, asset_id und maintenance_event_id möglich."] });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      await writeFormValues(ctx.current.formId, {
        asset_id: ctx.current.assetId,
        maintenance_event_id: ctx.current.eventId,
        values: collectFlatValues(fields, values),
        grade_manual: gradeManual || null,
        grade_override_reason: gradeReason || null,
        completion_status: "completed",
      });
      setFeedback({ tone: "success", messages: ["Daten wurden erfolgreich gespeichert."] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { detail?: { errors?: { message?: string }[] } } | null) : null;
      const serverErrors = Array.isArray(body?.detail?.errors)
        ? body!.detail!.errors!.map((e) => e?.message || "Servervalidierung fehlgeschlagen.")
        : [err instanceof ApiError ? err.message : "Persistenz fehlgeschlagen. Bitte später erneut versuchen."];
      setFeedback({ tone: "error", messages: serverErrors });
    } finally {
      setSaving(false);
    }
  }

  if (fatalError) {
    return (
      <AppShell title="Wartungsformular" back={{ to: "/wartungen", label: "zurück zur Übersicht" }}>
        <Alert variant="error">{fatalError}</Alert>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={
        viewOnly
          ? "Wartung ansehen"
          : ctx.current.typeKey
            ? `Wartungsformular: ${ctx.current.typeKey}`
            : "Wartungsformular"
      }
      subtitle={resolutionLabel ? `Verwendetes Formular: ${resolutionLabel} · ${fields.length} Feld(er)` : undefined}
      back={{ to: returnTo || "/wartungen", label: returnTo ? "zurück" : "zurück zur Übersicht" }}
    >
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Wartungsformular …
        </p>
      ) : (
        <div className="space-y-6">
          {viewOnly ? (
            <Alert variant="info">Diese Wartung ist nur zum Ansehen geöffnet.</Alert>
          ) : isEditingCompleted ? (
            <Alert variant="info">Abgeschlossene Wartung: Signatur muss erneut geleistet werden.</Alert>
          ) : null}
          {assetSummary ? (
            <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span>
                  <strong className="text-label">Kennung:</strong> {assetSummary.external_id || "–"}
                </span>
                <span>
                  <strong className="text-label">Name:</strong> {assetSummary.name || "–"}
                </span>
                <span>
                  <strong className="text-label">Letzte Wartung:</strong>{" "}
                  {assetSummary.last_performed_at
                    ? new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(
                        new Date(assetSummary.last_performed_at),
                      )
                    : "Noch keine Wartung vorhanden"}
                </span>
              </div>
            </div>
          ) : null}

          {feedback ? (
            <Alert variant={feedback.tone === "success" ? "success" : feedback.tone === "warning" ? "warning" : "error"}>
              {feedback.messages.length === 1 ? (
                feedback.messages[0]
              ) : (
                <ul className="list-inside list-disc">
                  {feedback.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              )}
            </Alert>
          ) : null}

          {masterdataFields.length ? (
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <h3 className="mb-3 text-base font-semibold text-label">Stammdaten</h3>
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {masterdataFields.map((field) => (
                  <div key={field.field_key} className="flex justify-between gap-4 border-b border-separator/50 py-1">
                    <dt className="text-sm text-label-secondary">{getFieldLabel(field)}</dt>
                    <dd className="text-sm text-label">
                      {formatFieldDisplayValue(field, values.masterdata?.[field.field_key])}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          {viewOnly ? (
            <ReadonlyMaintenance
              fields={fields}
              values={values}
              grade={effectiveGrade}
              autoGrade={autoGrade}
              manualGrade={manualGrade}
              overrideReason={gradeReason}
            />
          ) : fields.length === 0 ? (
            <p className="text-sm text-label-secondary">Dieses Formular enthält noch keine Felder.</p>
          ) : (
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-label">Inspektion</h3>
                {totalSteps > 1 ? (
                  <span className="text-xs text-label-secondary">
                    Schritt {safeStep + 1} / {totalSteps}
                  </span>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {(pages[safeStep] || [])
                  .filter((field) => field.resolved_visible)
                  .map((field) => (
                    <RuntimeField
                      key={field.field_key}
                      field={field}
                      value={values[field.effective_scope]?.[field.field_key]}
                      error={fieldErrors[field.field_key]}
                      onChange={(v) => setFieldValue(field, v)}
                    />
                  ))}
              </div>

              {totalSteps > 1 ? (
                <div className="mt-4 flex gap-2">
                  <Button type="button" variant="secondary" disabled={safeStep === 0} onClick={() => setCurrentStep(safeStep - 1)}>
                    Zurück
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={safeStep >= totalSteps - 1}
                    onClick={() => setCurrentStep(safeStep + 1)}
                  >
                    Weiter
                  </Button>
                </div>
              ) : null}
            </section>
          )}

          {viewOnly ? null : (
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <h3 className="mb-3 text-base font-semibold text-label">Bewertung</h3>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <span className="eh-label text-xs">Ermittelte Note</span>
                  <div className="mt-1">
                    {effectiveGrade != null ? (
                      <span
                        className="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold text-white"
                        style={{ backgroundColor: getGradeColor(effectiveGrade) }}
                      >
                        Note {effectiveGrade}
                      </span>
                    ) : (
                      <span className="text-sm text-label-secondary">Keine Note</span>
                    )}
                  </div>
                </div>
                <label className="space-y-1">
                  <span className="eh-label text-xs">Manuelle Korrektur</span>
                  <select className="eh-input w-40" value={gradeManual} onChange={(e) => setGradeManual(e.target.value)}>
                    <option value="">Automatisch ({autoGrade ?? "–"})</option>
                    {enabledGrades.map((g) => (
                      <option key={g} value={String(g)}>
                        Note {g}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex-1 space-y-1">
                  <span className="eh-label text-xs">Begründung</span>
                  <textarea
                    className="eh-input w-full"
                    rows={2}
                    placeholder="Optionaler Hinweis zur manuellen Notenkorrektur"
                    value={gradeReason}
                    onChange={(e) => setGradeReason(e.target.value)}
                  />
                </label>
              </div>
            </section>
          )}

          {viewOnly ? null : (
            <div>
              <Button type="button" disabled={saving} onClick={handleSave}>
                {saving ? "Speichere …" : "Wartung speichern"}
              </Button>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
